import { createI18n } from 'vue-i18n';
import { useNavigatorLanguage, useLocalStorage } from '@vueuse/core';
import de from '@/locales/de';
import en from '@/locales/en';
import { type Plugin, watch } from 'vue';

export type SupportedLanguage = 'en' | 'de';
export type MessageSchema = typeof de & typeof en;

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'de'];
export const FALLBACK_LANGUAGE: SupportedLanguage = 'de';

export const LANGUAGE_KEY = 'locale';

const plugin: Plugin = {
  install: (app) => {
    const locale = determineLanguage();

    const i18n = createI18n<[MessageSchema], SupportedLanguage, false>({
      legacy: false,
      globalInjection: true,
      locale,
      fallbackLocale: FALLBACK_LANGUAGE,
      messages: { de, en },
    });

    // Watch for locale changes and persist to localStorage
    i18n.global.locale.value = locale || i18n.global.locale.value;
    watch(
      () => i18n.global.locale.value,
      (newLocale) => {
        useLocalStorage<SupportedLanguage>(LANGUAGE_KEY, newLocale).value =
          newLocale;
      },
      { immediate: true },
    );

    app.provide('i18n', i18n);
    app.use(i18n);
  },
};

/** 
 * Determines the user's language preference based on localStorage or browser settings.
 *
 * Goes through the following steps:
 * 1. Checks localStorage for a previously set language.
 * 2. If not found, checks the browser's navigator language settings.
 * 3. If the browser's language is not supported, falls back to a predefined default language.

 * @returns {SupportedLanguage} The determined or fallback language.
 */
function determineLanguage(): SupportedLanguage {
  const secondChoice = determineSecondChoice();

  const storedLocale = useLocalStorage<SupportedLanguage>(
    LANGUAGE_KEY,
    secondChoice,
  );

  return storedLocale.value;
}

/**
 * Determines the second choice language based on the browser's navigator settings.
 * If the browser's language is not supported, it falls back to a predefined default language.
 * @returns {SupportedLanguage} The detected or fallback language.
 */
function determineSecondChoice() {
  const { language } = useNavigatorLanguage();
  if (!language.value) {
    return FALLBACK_LANGUAGE;
  }

  // Extract base language code (e.g., 'en' from 'en-US')
  const baseLanguage = language.value.split('-')[0] as SupportedLanguage;

  // Set and return either the detected language or fallback
  return SUPPORTED_LANGUAGES.includes(baseLanguage)
    ? baseLanguage
    : FALLBACK_LANGUAGE;
}

export default plugin;
